import React, { createContext, useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { signIn, signOut, getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import '../context/amplifyConfig';
import ForgotPassword from '../components/ForgotPassword';

export const CognitoAuthContext = createContext();

export const CognitoAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');

  // Check if user is already logged in on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] || [];
      
      // Get custom attributes from ID token payload (custom attributes are in ID token, not fetchUserAttributes)
      const idTokenPayload = session.tokens?.idToken?.payload || {};
      const attributes = await fetchUserAttributes();
      const party = idTokenPayload['custom:party'] || null;
      const organization = idTokenPayload['custom:organization'] || null;
      const name = attributes.name || idTokenPayload.name || null;
      const email = attributes.email || idTokenPayload.email || null;
      
      console.log('ID Token payload:', idTokenPayload);
      console.log('Party:', party, 'Organization:', organization);
      
      setUser({
        username: currentUser.username,
        email: email,
        name: name,
        party: party,
        organization: organization,
        ...currentUser
      });
      setUserGroups(groups);
      setLoggedIn(true);
      setShowLoginModal(false);
    } catch (error) {
      // Not logged in - this is expected, don't log as error
      setUser(null);
      setUserGroups([]);
      setLoggedIn(false);
      setShowLoginModal(true);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const { email, password } = event.target.elements;
    setIsLoggingIn(true);
    setLoginError('');

    try {
      const { isSignedIn } = await signIn({
        username: email.value,
        password: password.value
      });

      if (isSignedIn) {
        await checkAuthState();
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(error.message || 'Failed to login');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setUser(null);
      setUserGroups([]);
      setLoggedIn(false);
      setShowLoginModal(true);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const hasRole = (role) => {
    if (role === 'trader') {
      return userGroups.includes('trader') || userGroups.includes('user');
    }
    return userGroups.includes(role);
  };

  const isAdmin = () => hasRole('admin');
  const isManager = () => hasRole('manager');
  const isBackoffice = () => hasRole('backoffice');
  const isTrader = () => hasRole('trader');
  return (
    <CognitoAuthContext.Provider
      value={{
        user,
        userGroups,
        loggedIn,
        handleLogout,
        hasRole,
        isAdmin,
        isManager,
        isBackoffice,
        isTrader,
        checkAuthState
      }}
    >
      {children}

      {/* Login Modal */}
      <Modal show={showLoginModal} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Login to Absolute Collateral</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loginError && <Alert variant="danger">{loginError}</Alert>}
          
          <Form onSubmit={handleLogin}>
            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                required
                autoComplete="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password"
                required
                autoComplete="current-password"
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button
                variant="primary"
                type="submit"
                disabled={isLoggingIn}
              >
                {isLoggingIn ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>

              <Button
                variant="link"
                onClick={() => setShowForgotPassword(true)}
                disabled={isLoggingIn}
              >
                Forgot Password?
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Forgot Password Modal */}
      <ForgotPassword
        show={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
        defaultEmail={loginEmail}
      />
    </CognitoAuthContext.Provider>
  );
};
